/**
 * Booking wizard — drives SesamiSDK to render an inline calendar, slots, and form.
 *
 * Steps:
 *   1. location  — pick a physical/virtual location (custom UI)
 *   2. service   — pick a service available at that location (custom UI)
 *   3. datetime  — month calendar + time slots inline (custom UI, real Sesami data)
 *   4. details   — customer form, submitted via sesami.book()
 *   5. confirm   — success summary
 *
 * All booking data flows through SesamiSDK (cdn.sesami.co/sdk.js) — the script
 * is loaded async in theme.liquid and we wait for window.SesamiSDK to appear.
 */

const STEPS = ['location', 'service', 'datetime', 'details', 'confirm'];
const SDK_WAIT_TIMEOUT_MS = 10000;

class MBookingWizard extends HTMLElement {
  connectedCallback() {
    this.config = this.parseConfig();
    this.state = {
      step: 'location',
      selectedLocation: null,
      selectedService: null,
      selectedDay: null,
      selectedSlot: null,
      sesami: null,
      isSubmitting: false,
    };

    this.cacheElements();
    this.bindEvents();
    this.setStep('location');
    this.applyLinkPreset();
  }

  /* PDP "Reserve your viewing" links here with ?location=&service=&viewing=<handle>
     to skip straight to the calendar with the ring's photo and name on the form. */
  applyLinkPreset() {
    const q = new URLSearchParams(location.search);
    const viewing = (q.get('viewing') || '').trim().slice(0, 200);
    const field = this.querySelector('[data-viewing-field]');
    if (viewing && field) {
      const input = field.querySelector('input');
      const nameEl = field.querySelector('[data-viewing-name]');
      input.value = nameEl.textContent = (q.get('name') || '').trim().slice(0, 200) || viewing;
      field.hidden = false;
      if (/^[a-z0-9-]+$/.test(viewing)) {
        fetch(`/products/${viewing}.js`)
          .then((r) => (r.ok ? r.json() : null))
          .then((p) => {
            if (!p) return;
            // The product page sends the label name (metafields); the title is the fallback.
            input.value = nameEl.textContent = (q.get('name') || '').trim().slice(0, 200) || p.title;
            this.viewingUrl = `${location.origin}/products/${p.handle}`;
            if (!p.featured_image) return;
            this.viewingPhoto = new URL(p.featured_image, `https://${location.host}`).href;
            const img = field.querySelector('img');
            img.src = `${this.viewingPhoto}${this.viewingPhoto.includes('?') ? '&' : '?'}width=240`;
            img.alt = nameEl.textContent;
            img.hidden = false;
          })
          .catch(() => {});
      }
    }
    const byName = (cards, key, name) =>
      name && cards.find((c) => !c.hidden && (c.dataset[key] || '').trim().toLowerCase() === name.trim().toLowerCase());
    const loc = byName(this.locationCards, 'locationName', q.get('location'));
    if (!loc) return;
    this.onLocationPicked(loc);
    const svc = byName(this.serviceCards, 'serviceName', q.get('service'));
    if (svc) this.onServicePicked(svc);
  }

  parseConfig() {
    try {
      return JSON.parse(this.dataset.config || '{}');
    } catch (e) {
      console.error('[m-booking-wizard] invalid config JSON', e);
      return {};
    }
  }

  cacheElements() {
    this.progress = this.querySelector('.m-booking-wizard__progress');
    this.stepEls = Array.from(this.querySelectorAll('.m-booking-wizard__step'));
    this.serviceCards = Array.from(this.querySelectorAll('[data-service-id]'));
    this.stepAnnouncer = this.querySelector('[data-step-announcer]');
    this.locationCards = Array.from(this.querySelectorAll('[data-location-id]'));

    this.monthLabelEl = this.querySelector('[data-month-label]');
    this.dayNamesEl = this.querySelector('[data-day-names]');
    this.daysEl = this.querySelector('[data-days]');
    this.calendarLoadingEl = this.querySelector('[data-calendar-loading]');
    this.slotsWrapEl = this.querySelector('[data-slots-wrap]');
    this.slotsEl = this.querySelector('[data-slots]');
    this.slotsEmptyEl = this.querySelector('[data-slots-empty]');
    this.slotsPlaceholderEl = this.querySelector('[data-slots-placeholder]');
    this.selectedDateLabelEl = this.querySelector('[data-selected-date-label]');

    this.detailsSummaryEl = this.querySelector('[data-details-summary]');
    this.formEl = this.querySelector('[data-form]');
    this.formErrorEl = this.querySelector('[data-form-error]');
    this.submitEl = this.querySelector('[data-submit]');
    this.submitLabelEl = this.querySelector('[data-submit-label]');
    this.orderNumberFieldEl = this.querySelector('[data-order-number-field]');

    this.confirmSummaryEl = this.querySelector('[data-confirm-summary]');
  }

  bindEvents() {
    this.locationCards.forEach((card) => {
      card.addEventListener('click', () => this.onLocationPicked(card));
    });
    this.serviceCards.forEach((card) => {
      card.addEventListener('click', () => this.onServicePicked(card));
    });
    this.querySelectorAll('[data-action="back"]').forEach((btn) => {
      btn.addEventListener('click', () => this.goBack());
    });
    this.querySelectorAll('[data-action="prev-month"]').forEach((btn) => {
      btn.addEventListener('click', () => this.changeMonth(-1));
    });
    this.querySelectorAll('[data-action="next-month"]').forEach((btn) => {
      btn.addEventListener('click', () => this.changeMonth(1));
    });
    // Clicking a completed progress item jumps back to that step.
    this.querySelectorAll('.m-booking-wizard__progress-item').forEach((item) => {
      item.addEventListener('click', (event) => {
        if (item.dataset.state !== 'done') return;
        // Only act on taps on the visible dot/label, not stray clicks bubbled
        // from elsewhere — guards against Android tap-through into form inputs.
        if (!event.target.closest('.m-booking-wizard__progress-dot, .m-booking-wizard__progress-label')) {
          return;
        }
        this.setStep(item.dataset.step);
      });
    });
    if (this.formEl) {
      this.formEl.addEventListener('submit', (e) => {
        e.preventDefault();
        this.onFormSubmit();
      });
    }
    // Strip any leftover hash on load so the URL stays clean (e.g. /pages/book-appointment).
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }

  /* ─── State machine ──────────────────────────────────────────────── */

  canEnterStep(step) {
    if (step === 'location') return true;
    if (step === 'service') return !!this.state.selectedLocation;
    if (step === 'datetime') return !!this.state.selectedService;
    if (step === 'details') return !!this.state.selectedSlot;
    if (step === 'confirm') return !!this.state.selectedSlot;
    return false;
  }

  setStep(step) {
    if (!STEPS.includes(step)) return;
    this.state.step = step;
    this.stepEls.forEach((el) => {
      el.dataset.active = el.dataset.step === step ? 'true' : 'false';
    });
    this.updateProgress(step);
    this.announceStep(step);
    this.focusStep(step);
  }

  announceStep(step) {
    if (!this.stepAnnouncer || !this._didInitialFocus) return;
    const labels = {
      location: 'Step 1 of 4: Location',
      service: 'Step 2 of 4: Service',
      datetime: 'Step 3 of 4: Date and time',
      details: 'Step 4 of 4: Your details',
      confirm: 'Booking confirmed',
    };
    this.stepAnnouncer.textContent = labels[step] || '';
  }

  updateProgress(currentStep) {
    if (!this.progress) return;
    this.progress.dataset.currentStep = currentStep;
    // Confirm step doesn't appear on the progress bar — keep "details" lit.
    const progressKey = currentStep === 'confirm' ? 'details' : currentStep;
    const order = ['location', 'service', 'datetime', 'details'];
    const currentIndex = order.indexOf(progressKey);

    this.progress.querySelectorAll('.m-booking-wizard__progress-item').forEach((item) => {
      const itemIndex = order.indexOf(item.dataset.step);
      if (itemIndex < currentIndex) item.dataset.state = 'done';
      else if (itemIndex === currentIndex) item.dataset.state = 'current';
      else item.dataset.state = 'upcoming';
    });

    // Brilliant Earth-style line fill: width = currentStepIndex / (totalSteps - 1).
    // Step 1 (location) = 0%, step 2 = 33%, step 3 = 66%, step 4 = 100%.
    const fill = this.progress.querySelector('[data-progress-fill]');
    if (fill) {
      const segments = order.length - 1;
      const fillPct = Math.max(0, Math.min(1, currentIndex / segments)) * 100;
      fill.style.width = `${fillPct}%`;
    }
  }

  updateProgressSubtitle(stepKey, text) {
    if (!this.progress) return;
    const sub = this.progress.querySelector(`[data-progress-subtitle="${stepKey}"]`);
    if (sub) sub.textContent = text || '';
  }

  focusStep(step) {
    if (!this._didInitialFocus) {
      this._didInitialFocus = true;
      return;
    }
    const target = this.stepEls.find((el) => el.dataset.step === step);
    if (!target) return;
    const rect = target.getBoundingClientRect();
    if (rect.top >= 0) return;
    const headerOffset = (this.progress && this.progress.offsetHeight) || 0;
    const top = rect.top + window.scrollY - headerOffset - 16;
    window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
  }

  goBack() {
    const idx = STEPS.indexOf(this.state.step);
    if (idx > 0) this.setStep(STEPS[idx - 1]);
  }

  /* ─── Step 1: Location ───────────────────────────────────────────── */

  onLocationPicked(card) {
    this.state.selectedLocation = {
      id: card.dataset.locationId,
      name: card.dataset.locationName,
    };
    this.locationCards.forEach((c) => {
      c.dataset.selected = c === card ? 'true' : 'false';
    });
    this.updateProgressSubtitle('location', this.state.selectedLocation.name);
    this.filterServicesForLocation(this.state.selectedLocation.name);
    // Brief pause so the user sees the selected-state animation register
    // before the step swaps — feels less abrupt on tap.
    setTimeout(() => this.setStep('service'), 150);
  }

  filterServicesForLocation(locationName) {
    const normalised = (locationName || '').trim().toLowerCase();
    this.serviceCards.forEach((card) => {
      const allowed = (card.dataset.availableLocations || '').trim();
      if (!allowed) {
        card.hidden = false;
        return;
      }
      const list = allowed.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
      card.hidden = !list.includes(normalised);
    });

    // After hiding individual cards, hide any category whose entire group is empty —
    // otherwise we'd render a "Services" heading + subheading with no cards beneath.
    this.querySelectorAll('.m-booking-wizard__cards').forEach((cardsDiv) => {
      const cards = Array.from(cardsDiv.querySelectorAll('[data-service-id]'));
      if (cards.length === 0) return;
      const anyVisible = cards.some((c) => !c.hidden);
      cardsDiv.hidden = !anyVisible;
      // Walk back to also hide the preceding category heading + subheading (if any).
      let prev = cardsDiv.previousElementSibling;
      while (
        prev &&
        (prev.classList.contains('m-booking-wizard__category-description') ||
          prev.classList.contains('m-booking-wizard__category'))
      ) {
        prev.hidden = !anyVisible;
        prev = prev.previousElementSibling;
      }
    });
  }

  /* ─── Step 2: Service ────────────────────────────────────────────── */

  async onServicePicked(card) {
    const serviceData = this.findService(card.dataset.serviceId);
    if (!serviceData) return;
    this.state.selectedService = serviceData;
    this.state.selectedDay = null;
    this.state.selectedSlot = null;

    this.serviceCards.forEach((c) => {
      c.dataset.selected = c === card ? 'true' : 'false';
    });
    this.updateProgressSubtitle('service', serviceData.name);

    // Brief pause so the selected-state animation registers before swap.
    await new Promise((resolve) => setTimeout(resolve, 150));
    this.setStep('datetime');

    try {
      await this.initSesami(serviceData);
      this.autoAdvanceCount = 0;
      this.renderCalendarAndAutoAdvance();
    } catch (err) {
      console.error('[m-booking-wizard] sesami init failed', err);
      this.showCalendarError("We couldn't load availability. Please try again or get in touch.");
    }
  }

  /**
   * Render the calendar, then wait for every day's getAvailabilities() promise
   * to settle. Only after all data is in do we check whether any day became
   * `--available`. If the entire month is empty — typical at end-of-month when
   * remaining slots have passed — auto-advance to the next month. Caps at
   * 3 forward jumps so we don't loop forever when the service has no future
   * availability configured.
   */
  async renderCalendarAndAutoAdvance() {
    this.renderCalendar();
    const promises = this.dayAvailabilityPromises || [];
    await Promise.allSettled(promises);
    if (!this.daysEl) return;
    const hasAvailable = this.daysEl.querySelector('.m-booking-wizard__day--available');
    if (hasAvailable) return;
    if ((this.autoAdvanceCount || 0) >= 3) return;
    this.autoAdvanceCount = (this.autoAdvanceCount || 0) + 1;
    this.changeMonth(1);
  }

  findService(serviceId) {
    return (this.config.services || []).find((s) => s.id === serviceId) || null;
  }

  /**
   * Resolve the Shopify product + variant IDs to use for the SesamiSDK based on
   * the currently selected location. The merchant configures one product per
   * (service, location) pair in Sesami so that the booking confirmation email
   * picks up the correct Location/Description (e.g. York shop address vs. a
   * Google Meet URL for Virtual).
   *
   * Lookup order:
   *   1. Per-location IDs (yorkProductId / virtualProductId) when available.
   *   2. Block's `product` picker (Shopify product reference).
   *   3. Generic sesamiProductId fallback (legacy single-location config).
   */
  resolveServiceIds(service) {
    const locationName = (this.state.selectedLocation && this.state.selectedLocation.name) || '';
    const isVirtual = /virtual/i.test(locationName);
    const isYork = /york/i.test(locationName);

    if (isVirtual && service.virtualProductId) {
      return { productId: service.virtualProductId, variantId: service.virtualVariantId };
    }
    if (isYork && service.yorkProductId) {
      return { productId: service.yorkProductId, variantId: service.yorkVariantId };
    }
    if (service.productId) {
      return { productId: service.productId, variantId: service.variantId };
    }
    return { productId: service.sesamiProductId, variantId: service.sesamiVariantId };
  }

  /* ─── Sesami SDK ─────────────────────────────────────────────────── */

  async initSesami(service) {
    await this.waitForSdk();
    const ids = this.resolveServiceIds(service);
    const productId = ids.productId;
    const variantId = ids.variantId || productId;
    if (!productId) {
      throw new Error('Service has no productId for the selected location — configure York/Virtual IDs in the section settings.');
    }
    this.state.sesami = new window.SesamiSDK({
      shopId: String(this.config.shopId),
      productId: String(productId),
      variantId: String(variantId),
      quantity: 1,
      autoLoad: true,
      weekStartDay: 1, // Monday-first
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/London',
    });
    await this.state.sesami.init();
  }

  waitForSdk() {
    if (window.SesamiSDK) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const start = Date.now();
      const check = () => {
        if (window.SesamiSDK) return resolve();
        if (Date.now() - start > SDK_WAIT_TIMEOUT_MS) {
          return reject(new Error('SesamiSDK script did not load in time.'));
        }
        setTimeout(check, 50);
      };
      check();
    });
  }

  /* ─── Step 3: Calendar render ────────────────────────────────────── */

  renderCalendar() {
    const sesami = this.state.sesami;
    if (!sesami || !sesami.data) return [];
    this.dayAvailabilityPromises = [];

    if (this.monthLabelEl) {
      const now = sesami.now || new Date();
      this.monthLabelEl.textContent = new Intl.DateTimeFormat('en-GB', {
        month: 'long', year: 'numeric',
      }).format(now);
    }

    if (this.dayNamesEl) {
      const fmt = new Intl.DateTimeFormat('en-GB', { weekday: 'short' });
      this.dayNamesEl.innerHTML = '';
      sesami.data.labels.forEach((label) => {
        const li = document.createElement('div');
        li.className = 'm-booking-wizard__day-name';
        li.textContent = fmt.format(new Date(label)).slice(0, 2);
        this.dayNamesEl.appendChild(li);
      });
    }

    if (this.daysEl) {
      this.daysEl.innerHTML = '';
      sesami.data.rows.forEach((row) => {
        row.forEach((day) => {
          const cell = this.renderDayCell(day);
          this.daysEl.appendChild(cell);
        });
      });
    }

    // Reset slots area when month changes.
    this.clearSlots();
  }

  renderDayCell(day) {
    const cell = document.createElement('div');
    cell.className = 'm-booking-wizard__day';
    cell.setAttribute('role', 'gridcell');

    if (!day.isCurrentMonth) {
      cell.classList.add('m-booking-wizard__day--empty');
      return cell;
    }

    cell.textContent = new Date(day.date).getDate();
    if (day.isToday) cell.classList.add('m-booking-wizard__day--today');
    if (day.isPast) {
      cell.classList.add('m-booking-wizard__day--past');
      return cell;
    }

    // Tentatively render as unavailable, then upgrade once availability data resolves.
    cell.classList.add('m-booking-wizard__day--unavailable');

    // Attach once — Sesami fires onLoad multiple times. CSS gates via pointer-events.
    cell.addEventListener('click', () => this.onDayClick(day, cell));

    const checkAvailability = (slots) => {
      if (slots && slots.length > 0) {
        cell.classList.remove('m-booking-wizard__day--unavailable');
        cell.classList.add('m-booking-wizard__day--available');
        cell.setAttribute('aria-label', `${cell.textContent}, ${slots.length} slots available`);
      }
    };

    // autoLoad fires onLoad with availability data. getAvailabilities() also returns it.
    if (typeof day.onLoad === 'function') day.onLoad(checkAvailability);
    if (typeof day.getAvailabilities === 'function') {
      const p = day.getAvailabilities().then(checkAvailability).catch(() => {});
      if (this.dayAvailabilityPromises) this.dayAvailabilityPromises.push(p);
    }

    return cell;
  }

  onDayClick(day, cell) {
    this.state.selectedDay = day;
    // Toggle selected class on day cells.
    this.daysEl.querySelectorAll('.m-booking-wizard__day--selected').forEach((el) => {
      el.classList.remove('m-booking-wizard__day--selected');
    });
    cell.classList.add('m-booking-wizard__day--selected');

    this.renderSlotsForDay(day);
  }

  async renderSlotsForDay(day) {
    if (!this.slotsWrapEl || !this.slotsEl) return;
    this.slotsWrapEl.hidden = false;
    this.slotsEl.innerHTML = '';
    this.slotsEmptyEl.hidden = true;
    if (this.slotsPlaceholderEl) this.slotsPlaceholderEl.dataset.show = 'false';

    if (this.selectedDateLabelEl) {
      this.selectedDateLabelEl.textContent = this.formatLongDate(new Date(day.date));
    }

    let slots = [];
    try {
      slots = await day.getAvailabilities();
    } catch (e) {
      console.error('[m-booking-wizard] getAvailabilities failed', e);
    }

    if (!slots || slots.length === 0) {
      this.slotsEmptyEl.hidden = false;
      return;
    }

    // Dedupe by startTime — Sesami returns one slot per team member per time, but
    // the customer is picking a time, not a team member, so collapse to unique times.
    const seen = new Set();
    slots = slots.filter((s) => {
      const key = s.startTime;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    slots.forEach((slot) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'm-booking-wizard__slot';
      btn.setAttribute('role', 'listitem');
      const time = (slot.startTime || '').split(' ')[1] || slot.startTime;
      btn.textContent = time;
      btn.addEventListener('click', () => this.onSlotClick(slot));
      this.slotsEl.appendChild(btn);
    });
  }

  clearSlots() {
    if (this.slotsEl) this.slotsEl.innerHTML = '';
    if (this.slotsEmptyEl) this.slotsEmptyEl.hidden = true;
    if (this.selectedDateLabelEl) this.selectedDateLabelEl.textContent = '';
    if (this.slotsPlaceholderEl) this.slotsPlaceholderEl.dataset.show = 'true';
  }

  changeMonth(direction) {
    if (!this.state.sesami) return;
    if (this.calendarLoadingEl) this.calendarLoadingEl.hidden = false;
    const action = direction > 0 ? this.state.sesami.next : this.state.sesami.previous;
    Promise.resolve(action.call(this.state.sesami, 'month')).finally(() => {
      if (this.calendarLoadingEl) this.calendarLoadingEl.hidden = true;
      // When the user clicks prev/next we don't auto-advance again; only the
      // initial render auto-advances to skip empty months.
      if (direction > 0 && this.autoAdvanceCount > 0 && this.autoAdvanceCount < 3) {
        this.renderCalendarAndAutoAdvance();
      } else {
        this.renderCalendar();
      }
    });
  }

  showCalendarError(msg) {
    if (this.daysEl) this.daysEl.innerHTML = `<p class="m-booking-wizard__error">${this.escapeHtml(msg)}</p>`;
  }

  /* ─── Step 4: Details form ───────────────────────────────────────── */

  onSlotClick(slot) {
    this.state.selectedSlot = slot;
    const day = this.state.selectedDay;
    const time = (slot.startTime || '').split(' ')[1] || slot.startTime;
    if (day) {
      this.updateProgressSubtitle('datetime', `${this.formatLongDate(new Date(day.date))} · ${time}`);
    }
    this.renderDetailsSummary();
    this.toggleOrderNumberField();
    this.updateNotesLabel();
    this.setStep('details');
  }

  toggleOrderNumberField() {
    if (!this.orderNumberFieldEl) return;
    const requires = !!(this.state.selectedService && this.state.selectedService.requiresOrderNumber);
    this.orderNumberFieldEl.hidden = !requires;
    if (!requires) {
      const input = this.orderNumberFieldEl.querySelector('input');
      if (input) input.value = '';
    }
  }

  updateNotesLabel() {
    const labelEl = this.querySelector('[data-notes-label]');
    if (!labelEl) return;
    // Per-service override falls back to whatever the snippet rendered as the default.
    const override = (this.state.selectedService && this.state.selectedService.notesLabel || '').trim();
    if (!override) {
      // Restore the original default the snippet shipped with on first override.
      if (this.defaultNotesLabel == null) this.defaultNotesLabel = labelEl.textContent.trim();
      labelEl.textContent = this.defaultNotesLabel;
      return;
    }
    if (this.defaultNotesLabel == null) this.defaultNotesLabel = labelEl.textContent.trim();
    labelEl.textContent = override;
  }

  renderDetailsSummary() {
    if (!this.detailsSummaryEl) return;
    const svc = this.state.selectedService;
    const loc = this.state.selectedLocation;
    const slot = this.state.selectedSlot;
    const day = this.state.selectedDay;
    if (!svc || !loc || !slot || !day) return;
    const dateStr = this.formatLongDate(new Date(day.date));
    const time = (slot.startTime || '').split(' ')[1] || slot.startTime;
    this.detailsSummaryEl.innerHTML = `
      <div class="m-booking-wizard__summary-line">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
        <span>${this.escapeHtml(loc.name)}</span>
      </div>
      <div class="m-booking-wizard__summary-line m-booking-wizard__summary-line--service">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M12 .587l3.668 7.568L24 9.423l-6 5.85 1.42 8.276L12 19.771l-7.42 3.778L6 15.273 0 9.423l8.332-1.268z"/></svg>
        <span>${this.escapeHtml(svc.name)}</span>
      </div>
      <div class="m-booking-wizard__summary-line">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
        <span>${this.escapeHtml(dateStr)}</span>
      </div>
      <div class="m-booking-wizard__summary-line">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
        <span>${this.escapeHtml(time)}</span>
      </div>
    `;
  }

  readInspiration() {
    const q = new URLSearchParams(location.search);
    const photo = (q.get('inspiration_photo') || '').trim();
    return {
      title: (q.get('inspiration') || '').trim().slice(0, 200),
      /* Store photos only, so a crafted link cannot put an arbitrary URL on a
         booking. Live files come from the shop's own domain, not myshopify.com. */
      photo: this.storePhotoUrl(photo),
    };
  }

  storePhotoUrl(url) {
    if (!url) return '';
    try {
      const u = new URL(url, location.origin);
      const sameStore = u.host === location.host || /(^|\.)(cdn\.shopify\.com|myshopify\.com)$/.test(u.host);
      const isFile = u.pathname.startsWith('/cdn/') || u.pathname.startsWith('/s/files/');
      return u.protocol.startsWith('http') && sameStore && isFile ? u.href : '';
    } catch (e) {
      return '';
    }
  }

  async onFormSubmit() {
    if (this.state.isSubmitting) return;
    if (!this.formEl) return;
    const data = new FormData(this.formEl);
    const customer = {
      firstName: (data.get('firstName') || '').toString().trim(),
      lastName: (data.get('lastName') || '').toString().trim(),
      email: (data.get('email') || '').toString().trim(),
      phone: (data.get('phone') || '').toString().trim(),
    };
    const notes = (data.get('notes') || '').toString().trim();
    const orderNumber = (data.get('orderNumber') || '').toString().trim();
    const privacy = data.get('privacy');

    const normalizedPhone = customer.phone.replace(/[\s\-().]/g, '');
    const errors = [];
    if (!customer.firstName) errors.push('Please enter your first name.');
    if (!customer.lastName) errors.push('Please enter your last name.');
    if (!customer.email || !/^\S+@\S+\.\S+$/.test(customer.email)) errors.push('Please enter a valid email address.');
    if (!customer.phone) {
      errors.push('Please enter your phone number.');
    } else if (!/^\+[1-9]\d{7,14}$/.test(normalizedPhone)) {
      errors.push('Please enter your phone number with country code (e.g. +44 7912 345678).');
    }
    if (!privacy) errors.push('Please agree to the privacy policy.');
    if (errors.length) {
      this.showFormError(errors[0]);
      return;
    }
    customer.phone = normalizedPhone;

    const options = {
      properties: [
        { name: 'Location', value: this.state.selectedLocation.name },
        { name: 'Service', value: this.state.selectedService.name },
      ],
    };
    if (orderNumber) options.properties.push({ name: 'Order Number', value: orderNumber });
    /* Set when the visitor came from "Design a ring like this" in the
       inspiration viewer. It goes inside Notes because that is one of the few
       properties Sesami prints into the Google Calendar event, which is where
       the team reads its bookings. */
    const inspiration = this.readInspiration();
    const viewing = (data.get('viewing') || '').toString().trim();
    const note = [
      viewing && `Reserve a viewing: ${viewing}`,
      viewing && this.viewingUrl && `Ring: ${this.viewingUrl}`,
      viewing && this.viewingPhoto && `Ring photo: ${this.viewingPhoto}`,
      notes,
      inspiration.title && `Inspiration: ${inspiration.title}`,
      inspiration.photo && `Photo: ${inspiration.photo}`,
    ]
      .filter(Boolean)
      .join('\n');
    if (note) options.properties.push({ name: 'Notes', value: note });

    this.setSubmitting(true);
    this.showFormError(null);

    try {
      await this.state.sesami.book(this.state.selectedSlot, customer, options);
      // Create / update the Shopify customer card alongside the Sesami booking.
      // Fire-and-forget — booking has already succeeded; a customer-create
      // failure must not block the confirmation step.
      this.createShopifyCustomer(customer).catch((e) =>
        console.warn('[m-booking-wizard] customer create skipped:', e)
      );
      this.renderConfirmSummary(customer, { notes, orderNumber, viewing });
      this.setStep('confirm');
    } catch (err) {
      console.error('[m-booking-wizard] booking failed', err);
      const detail = await this.extractBookingErrorMessage(err);
      this.showFormError(
        detail
          ? `Sorry, we couldn't complete the booking: ${detail}`
          : "Sorry, we couldn't complete the booking. Please try a different time or get in touch."
      );
    } finally {
      this.setSubmitting(false);
    }
  }

  // Sesami throws the raw fetch Response on validation errors; read the body.
  async extractBookingErrorMessage(err) {
    if (typeof Response !== 'undefined' && err instanceof Response) {
      try {
        const data = await err.clone().json();
        if (data && typeof data.message === 'string') return data.message;
      } catch (_) {}
      try {
        const text = await err.clone().text();
        if (text) return text;
      } catch (_) {}
      return null;
    }
    if (err && typeof err.message === 'string') return err.message;
    return null;
  }

  /**
   * Create (or upsert) a Shopify customer card from the booking form.
   *
   * Submits the Liquid-rendered `{% form 'customer' %}` (sibling in the
   * section template) into a hidden iframe. Shopify renders that form
   * with its CAPTCHA / anti-bot plumbing baked in — required for the
   * customer record to actually be created on /contact. Plain JS-built
   * forms get silently dropped by Shopify's anti-bot.
   */
  async createShopifyCustomer(customer) {
    const form = this.querySelector('[data-customer-form]');
    if (!form) {
      console.warn('[m-booking-wizard] customer form not found in DOM');
      return;
    }

    const setField = (selector, value) => {
      const input = form.querySelector(selector);
      if (input) input.value = value;
    };

    setField('[data-customer-first-name]', customer.firstName);
    setField('[data-customer-last-name]', customer.lastName);
    setField('[data-customer-email]', customer.email);
    setField('[data-customer-phone]', customer.phone);

    return new Promise((resolve) => {
      const iframe = this.querySelector('iframe[name="m-booking-wizard-customer-frame"]');
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      if (iframe) iframe.addEventListener('load', () => setTimeout(finish, 500), { once: true });
      // Fallback in case the iframe load event doesn't fire.
      setTimeout(finish, 5000);
      form.submit();
    });
  }

  setSubmitting(isSubmitting) {
    this.state.isSubmitting = isSubmitting;
    if (this.submitEl) {
      this.submitEl.disabled = isSubmitting;
      this.submitEl.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');
    }
    if (this.submitLabelEl) {
      this.submitLabelEl.textContent = isSubmitting ? 'Booking…' : 'Complete Booking';
    }
  }

  showFormError(msg) {
    if (!this.formErrorEl) return;
    if (!msg) {
      this.formErrorEl.hidden = true;
      this.formErrorEl.textContent = '';
      return;
    }
    this.formErrorEl.hidden = false;
    this.formErrorEl.textContent = msg;
  }

  /* ─── Step 5: Confirm ────────────────────────────────────────────── */

  renderConfirmSummary(customer, extras = {}) {
    if (!this.confirmSummaryEl) return;
    const svc = this.state.selectedService;
    const loc = this.state.selectedLocation;
    const slot = this.state.selectedSlot;
    const day = this.state.selectedDay;
    const dateStr = this.formatLongDate(new Date(day.date));
    const time = (slot.startTime || '').split(' ')[1] || slot.startTime;
    const orderNumber = (extras.orderNumber || '').trim();
    const notes = (extras.notes || '').trim();
    const viewing = (extras.viewing || '').trim();
    this.confirmSummaryEl.innerHTML = `
      <dl class="m-booking-wizard__success-list">
        <div><dt>Location</dt><dd>${this.escapeHtml(loc.name)}</dd></div>
        <div><dt>Service</dt><dd>${this.escapeHtml(svc.name)}</dd></div>
        <div><dt>Date</dt><dd>${this.escapeHtml(dateStr)}</dd></div>
        <div><dt>Time</dt><dd>${this.escapeHtml(time)}</dd></div>
        <div><dt>Name</dt><dd>${this.escapeHtml(customer.firstName + ' ' + customer.lastName)}</dd></div>
        <div><dt>Email</dt><dd>${this.escapeHtml(customer.email)}</dd></div>
        ${viewing ? `<div><dt>Viewing</dt><dd>${this.escapeHtml(viewing)}</dd></div>` : ''}
        ${orderNumber ? `<div><dt>Order Number</dt><dd>${this.escapeHtml(orderNumber)}</dd></div>` : ''}
        ${notes ? `<div><dt>Notes</dt><dd>${this.escapeHtml(notes)}</dd></div>` : ''}
      </dl>
    `;
  }

  /* ─── Helpers ────────────────────────────────────────────────────── */

  formatLongDate(date) {
    const weekday = new Intl.DateTimeFormat('en-GB', { weekday: 'long' }).format(date);
    const day = date.getDate();
    const month = new Intl.DateTimeFormat('en-GB', { month: 'long' }).format(date);
    return `${weekday} ${day}${this.ordinal(day)} ${month}`;
  }

  ordinal(n) {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
  }

  escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    })[c]);
  }
}

if (!customElements.get('m-booking-wizard')) {
  customElements.define('m-booking-wizard', MBookingWizard);
}

export { MBookingWizard };
